/**
 * The decision writes in flight, by decision item id (I-1154).
 *
 * A write's own realtime echo can arrive before the write's answer does, on a loaded network. The
 * decision panel recognises an echo by the item's id and the revision it was recorded at, compared
 * with the revision of what the panel shows — and while a write is out it does not yet know the
 * revision the write's answer will bring. So a write announces itself here while it is out, the
 * panel holds the events that arrive meanwhile, and judges them once the write has settled.
 */
const inFlight = new Map<string, number>();
const settledListeners = new Set<(itemId: string) => void>();
/**
 * I-1270: per decision item, the highest work area revision one of this client's landed writes was
 * answered at (`IDecision.revision`). An event of the item at or below it is that write's echo.
 */
const landedRevisions = new Map<string, number>();

/** How many writes to this decision this client has out right now. */
export function decisionWritesInFlight(itemId: string): number {
  return inFlight.get(itemId) ?? 0;
}

/**
 * Whether an event of `itemId` at `revision` is the echo of one of this client's own landed decision
 * writes (I-1270, the T-0569 rule): the same item, at or below the revision that write's answer was
 * read at. A later revision, another item, or a write answered without a revision is a change
 * elsewhere.
 */
export function isOwnDecisionEcho(itemId: string, revision: number): boolean {
  const landed = landedRevisions.get(itemId);
  return landed !== undefined && revision <= landed;
}

/** Told when a write to a decision settled, answered or failed, after it stopped counting as out. */
export function onDecisionWriteSettled(listener: (itemId: string) => void): () => void {
  settledListeners.add(listener);
  return () => void settledListeners.delete(listener);
}

/** Runs a write to the decision `itemId` as one in flight; its settling is announced. */
export async function trackDecisionWrite<T>(
  itemId: string,
  write: Promise<T> | (() => Promise<T>),
): Promise<T> {
  inFlight.set(itemId, decisionWritesInFlight(itemId) + 1);
  try {
    const answer = await (typeof write === 'function' ? write() : write);
    const revision = (answer as { revision?: unknown } | null)?.revision;
    if (typeof revision === 'number') {
      landedRevisions.set(itemId, Math.max(landedRevisions.get(itemId) ?? 0, revision));
    }
    return answer;
  } finally {
    const left = decisionWritesInFlight(itemId) - 1;
    if (left > 0) inFlight.set(itemId, left);
    else inFlight.delete(itemId);
    settledListeners.forEach((listener) => listener(itemId));
  }
}

/**
 * Forgets every write and listener. For a test's setup, between tests: a write a test left in flight
 * (it threw before releasing it) must not hold the next test's events back.
 */
export function resetDecisionWritesForTests(): void {
  inFlight.clear();
  settledListeners.clear();
  landedRevisions.clear();
}
